import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send, Upload, X } from "lucide-react";
import { Reveal, Eyebrow } from "../components/anim";
import Starfield from "../components/Starfield";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_INFO = {
  basic: { name: "Basic", price: 300, pages: "2–3 pages" },
  standard: { name: "Standard", price: 450, pages: "Up to 5 pages" },
  premium: { name: "Premium", price: 650, pages: "Up to 7 pages" },
};

const VISITOR_GOALS = ["Call us", "Request a quote", "Book an appointment", "Visit us in person"];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-500 focus:border-blue-400";

const Field = ({ label, required, hint, children, testId }) => (
  <div data-testid={testId}>
    <label className="mb-1.5 block text-sm font-semibold text-slate-200">
      {label} {required ? <span className="text-blue-400">*</span> : <span className="ml-1 text-xs font-normal text-slate-500">(optional)</span>}
    </label>
    {children}
    {hint && <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{hint}</p>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="border-b border-white/10 pb-3 font-display text-lg font-bold tracking-tight text-white">
    {children}
  </h2>
);

export default function GetStarted() {
  const { pkg } = useParams();
  const info = PACKAGE_INFO[pkg];
  const [form, setForm] = useState({});
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  if (!info) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 pt-20" data-testid="onboarding-invalid">
        <div className="text-center">
          <p className="text-lg font-semibold text-white">That package link doesn't look right.</p>
          <Link to="/pricing" className="mt-3 inline-block text-sm font-semibold text-blue-400" data-testid="onboarding-invalid-back">
            View packages
          </Link>
        </div>
      </main>
    );
  }

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const data = new FormData();
      files.forEach((f) => data.append("files", f));
      const res = await axios.post(`${API}/uploads`, data);
      setUploads((u) => [...u, ...res.data.files]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — images or PDFs up to 10MB each.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/onboarding`, {
        package: pkg,
        origin_url: window.location.origin,
        ...form,
        uploads,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Couldn't submit — please try again or call (507) 841-1801.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 pt-20" data-testid="onboarding-success">
        <Starfield />
        <div className="relative w-full max-w-lg rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
            <CheckCircle2 className="h-9 w-9 text-emerald-400" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-bold text-white" data-testid="onboarding-success-message">
            Thank you! We've received your website details.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            G-Studio will review your information and contact you about the next steps.
          </p>
          <Link to="/" data-testid="onboarding-home-btn" className="mt-7 inline-block rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500">
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden pt-28 pb-20" data-testid="onboarding-page">
      <Starfield />
      <div className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-[130px]" />
      <div className="relative mx-auto max-w-2xl px-4 sm:px-6">
        <Reveal>
          <div className="flex justify-center"><Eyebrow>Website Request Form</Eyebrow></div>
          <div className="mt-5 text-center">
            <span className="inline-block rounded-full bg-blue-600 px-4 py-1.5 font-mono2 text-xs font-semibold uppercase tracking-[0.18em] text-white" data-testid="onboarding-package-badge">
              {info.name} Package
            </span>
            <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Tell us about your business.
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
              {info.name} package · {info.pages} · ${info.price} one-time. Only the essentials are required — you can
              send photos and details later.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="mt-10 space-y-8" data-testid="onboarding-form">
            <section className="space-y-5">
              <SectionTitle>Contact Information</SectionTitle>
              <Field label="Your name" required>
                <input required className={inputCls} value={form.name || ""} onChange={set("name")} data-testid="ob-name" />
              </Field>
              <Field label="Business name" required>
                <input required className={inputCls} value={form.business_name || ""} onChange={set("business_name")} data-testid="ob-business-name" />
              </Field>
              <Field label="Email used at checkout" required>
                <input required type="email" className={inputCls} value={form.email || ""} onChange={set("email")} data-testid="ob-email" />
              </Field>
              <Field label="Phone number">
                <input className={inputCls} value={form.phone || ""} onChange={set("phone")} data-testid="ob-phone" />
              </Field>
            </section>

            <section className="space-y-5">
              <SectionTitle>About Your Business</SectionTitle>
              <Field label="Business address or service area">
                <input className={inputCls} value={form.address || ""} onChange={set("address")} data-testid="ob-address" />
              </Field>
              <Field label="Business hours">
                <input className={inputCls} placeholder="e.g. Mon–Fri 9am–5pm" value={form.hours || ""} onChange={set("hours")} data-testid="ob-hours" />
              </Field>
              <Field label="Describe your business and services">
                <textarea rows={3} className={inputCls} value={form.description || ""} onChange={set("description")} data-testid="ob-description" />
              </Field>
              <Field label="How should your business come across on the website?" hint="Tell us what customers should know, what makes you stand out, and the impression you want to leave.">
                <textarea rows={4} className={inputCls} value={form.expression || ""} onChange={set("expression")} data-testid="ob-expression" />
              </Field>
              <Field label="What should visitors do?">
                <div className="flex flex-wrap gap-2" data-testid="ob-visitor-goal">
                  {VISITOR_GOALS.map((g) => (
                    <button
                      type="button"
                      key={g}
                      onClick={() => setForm({ ...form, visitor_goal: g })}
                      data-testid={`ob-goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                        form.visitor_goal === g
                          ? "border-blue-400 bg-blue-500/15 text-blue-300"
                          : "border-white/15 text-slate-300 hover:border-white/30"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </Field>
            </section>

            {(pkg === "standard" || pkg === "premium") && (
              <section className="space-y-5">
                <SectionTitle>Your Story & Highlights</SectionTitle>
                <Field label="Tell us about your owners, team, and business story">
                  <textarea rows={3} className={inputCls} value={form.story || ""} onChange={set("story")} data-testid="ob-story" />
                </Field>
                <Field label="Additional services, amenities, or features to highlight">
                  <textarea rows={3} className={inputCls} value={form.highlights || ""} onChange={set("highlights")} data-testid="ob-highlights" />
                </Field>
                <Field label="Customer testimonials you have permission to publish">
                  <textarea rows={3} className={inputCls} value={form.testimonials || ""} onChange={set("testimonials")} data-testid="ob-testimonials" />
                </Field>
                <Field label="Which email should receive website analytics access?">
                  <input type="email" className={inputCls} value={form.analytics_email || ""} onChange={set("analytics_email")} data-testid="ob-analytics-email" />
                </Field>
              </section>
            )}

            {pkg === "premium" && (
              <section className="space-y-5">
                <SectionTitle>AI Assistant Knowledge</SectionTitle>
                <Field label="What questions do customers frequently ask? Include the correct answers.">
                  <textarea rows={4} className={inputCls} value={form.faq_knowledge || ""} onChange={set("faq_knowledge")} data-testid="ob-faq-knowledge" />
                </Field>
                <Field label="What pricing, policies, service details, and other information should your AI assistant know?">
                  <textarea rows={4} className={inputCls} value={form.assistant_knowledge || ""} onChange={set("assistant_knowledge")} data-testid="ob-assistant-knowledge" />
                </Field>
                <Field label="What should the assistant direct customers to do when it cannot answer?">
                  <textarea rows={2} className={inputCls} value={form.assistant_fallback || ""} onChange={set("assistant_fallback")} data-testid="ob-assistant-fallback" />
                </Field>
                <Field label="What contact information should the assistant provide?">
                  <textarea rows={2} className={inputCls} value={form.assistant_contact || ""} onChange={set("assistant_contact")} data-testid="ob-assistant-contact" />
                </Field>
              </section>
            )}

            <section className="space-y-5">
              <SectionTitle>Files & Final Details</SectionTitle>
              <Field label="Upload your logo and photos" hint="Images or PDFs, up to 10MB each. You can also send them later by email.">
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf" onChange={onFiles} className="hidden" data-testid="ob-upload-input" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="ob-upload-btn"
                  className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-slate-300 transition-colors hover:border-blue-400 hover:text-blue-300 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Uploading…" : "Choose files"}
                </button>
                {uploads.length > 0 && (
                  <ul className="mt-3 space-y-1.5" data-testid="ob-upload-list">
                    {uploads.map((u, i) => (
                      <li key={u.path} className="flex items-center gap-2 text-xs text-slate-300">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                        <span className="truncate">{u.name}</span>
                        <button
                          type="button"
                          onClick={() => setUploads(uploads.filter((_, j) => j !== i))}
                          className="ml-auto text-slate-500 hover:text-white"
                          aria-label={`Remove ${u.name}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
              <Field label="Social media links">
                <textarea rows={2} className={inputCls} placeholder="Facebook, Instagram, LinkedIn…" value={form.social_links || ""} onChange={set("social_links")} data-testid="ob-social-links" />
              </Field>
              <Field label="Any additional requests?" hint="Requests outside your package may require a separate quote.">
                <textarea rows={3} className={inputCls} value={form.additional_requests || ""} onChange={set("additional_requests")} data-testid="ob-additional-requests" />
              </Field>
            </section>

            <button
              type="submit"
              disabled={sending || uploading}
              data-testid="ob-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-colors hover:bg-blue-500 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Submitting…" : "Submit My Website Details"}
            </button>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
